/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import React from 'react';

import { styled } from 'fierry/react.old/styled';

import { areEqual } from 'fierry/type/compare';

export { useDeferredValue, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore } from 'react';

export { css, styled, keyframes, createGlobalStyle } from 'fierry/react.old/styled';

export { default as useCallback } from 'fierry/flux/hook/use.callback';


const CONTEXT = Symbol.for('fierry.context');

const SELECTION = Symbol.for('fierry.selection');


// - - - - - Select


export const use = (...targets) =>
{
  return targets.length > 1 ? targets.map(useTarget) : useTarget(targets[0]);
};


export const useTarget = (target) =>
{
  const selection = isSelection(target);

  const context  = selection ? target.context  : target;
  const selector = selection ? target.selector : (value) => value;

  if (context.selectable)
  {
    return useSelectableContext(context, selector);
  }

  if (context.instance)
  {
    return useInstanceContext(context, selector);
  }

  return selector(React.use(context.reactContext ?? context));
};


export const useSelectableContext = (selectableContext, selector) =>
{
  const store = React.use(selectableContext.reactContext);

  const subscribe = React.useCallback((onStoreChange) =>
  {
    store.listeners.add(onStoreChange);

    return () =>
    {
      store.listeners.delete(onStoreChange);
    }
  }, [store]);

  return React.useSyncExternalStore(subscribe, () => selector(store.value));
};


export const useInstanceContext = (instanceContext, selector) =>
{
  const instance = React.use(instanceContext.reactContext);

  const subscribe = React.useCallback((listener) => instance ? instance.subscribe(listener) : () => {}, [ instance ]);

  return React.useSyncExternalStore(subscribe, () => instance ? selector(instance) : undefined);
};


const isSelection = (value) =>
{
  return value !== null && typeof value === 'object' && value.$$typeof === SELECTION;
};


// - - - - - Context


export const createStoreContext = () =>
{
  const Context = createContext();

  Context.Dispatch = createContext();

  return Context;
};


export const createContext = (defaultValue) =>
{
  const Context = React.createContext(defaultValue);

  return createCallableContext(Context, Context, false);
};


export const createSelectableContext = (defaultValue) =>
{
  const Context = React.createContext(defaultValue);

  const Provider = ({ value, children }) =>
  {
    const store = React.useRef({ value, listeners: new Set() }).current;

    React.useLayoutEffect(() =>
    {
      store.value = value;

      for (const listener of store.listeners)
      {
        listener();
      }
    }, [value]);

    return <Context value = {store} children = {children} />;
  };

  return createCallableContext(Context, Provider, true);
};


const createCallableContext = (Context, Provider, selectable) =>
{
  const CallableContext = (arg) =>
  {
    if (typeof arg === 'function')
    {
      return { $$typeof: SELECTION, context: CallableContext, selector: arg };
    }

    return <Provider value = {arg.value} children = {arg.children} />;
  };

  CallableContext.$$typeof = CONTEXT;
  CallableContext.reactContext = Context;
  CallableContext.selectable = selectable;

  return CallableContext;
};


export const isReactContext = (value) =>
{
  return value !== null && typeof value === 'object' && value.$$typeof === Symbol.for('react.context');
};


const isCallableContext = (value) =>
{
  return value !== null && typeof value === 'function' && value.$$typeof === CONTEXT;
};


// - - - - - Hooks


export const useResolvedProps = (props) =>
{
  const toResolvedValue = (value) =>
  {
    const shouldResolve = isReactContext(value) || isCallableContext(value) || isSelection(value);

    return shouldResolve ? use(value) : value;
  };

  const toResolvedProp = ([ key, value ]) =>
  {
    return [ key, Array.isArray(value) ? value.map(toResolvedValue) : toResolvedValue(value) ];
  };

  return useStableProps(Object.fromEntries(Object.entries(props).map(toResolvedProp)));
};


export const useStableProps = (values) =>
{
  const ref = React.useRef(values);

  const isStable = (value, prev) =>
  {
    if (areEqual(value, prev))
    {
      return true;
    }

    return Array.isArray(value) && Array.isArray(prev) && value.length === prev.length && value.every((element, index) =>
    {
      return areEqual(element, prev[index]);
    });
  };

  const toStableValue = ([ key, value ]) =>
  {
    return [ key, isStable(value, ref.current[key]) ? ref.current[key] : value ];
  };

  const stable = Object.fromEntries(Object.entries(values).map(toStableValue));

  React.useLayoutEffect(() =>
  {
    ref.current = stable;
  });

  return stable;
};


export const useClient = (ContextOrClass, props) =>
{
  props = useResolvedProps(props);

  const Class = ContextOrClass.Class || ContextOrClass;

  return React.useMemo(() => new Class(props), Object.values(props));
};


export const useValue = (callback, props) =>
{
  props = useResolvedProps(props);

  return React.useMemo(() => callback(props), Object.values(props));
};


export const useStore = (defaultStateCallback, reducers, props) =>
{
  props = useResolvedProps(props);

  const onChange = (state, action) =>
  {
    return resolveReducer(reducers, action)(state, action.args, props);
  };

  const [ reactState, reactDispatch ] = React.useReducer(onChange, defaultStateCallback());

  return [ reactState, useDispatchObject(reducers, reactDispatch) ];
};


export const useAsyncStore = (defaultStateCallback, reducers, props) =>
{
  props = useResolvedProps(props);

  const onChange = async (state, action) =>
  {
    return await resolveReducer(reducers, action)(state, action.args, props);
  };

  const [ reactState, reactDispatch, isPending ] = React.useActionState(onChange, defaultStateCallback());

  const onDispatch = (action) =>
  {
    return React.startTransition(() => reactDispatch(action));
  };

  return [ useMerged(reactState, { isPending }), useDispatchObject(reducers, onDispatch) ];
};


const useDispatchObject = (reducers, onDispatch) =>
{
  const createDispatch = () =>
  {
    const toActionDispatch = (type) =>
    {
      return [ type, (args) => onDispatch({ type, args }) ];
    };

    return Object.fromEntries(Object.keys(reducers).map(toActionDispatch));
  }

  return React.useMemo(createDispatch, [ reducers ]);
};


export const useStable = (value) =>
{
  const ref = React.useRef(value);

  const isEqual = ref.current.equals(value);

  React.useLayoutEffect(() =>
  {
    if (!isEqual)
    {
      ref.current = value;
    }
  }, [ value ]);

  return isEqual ? ref.current : value;
};


export const useMerged = (object, extraProps) =>
{
  return React.useMemo(() => ({ ...object, ...extraProps }), [object, ...Object.values(extraProps)]);
};


// - - - - - Decorators


export const withStyled = (Component) =>
{
  return styled(Component)``;
};


export const withMemo = (Component) =>
{
  const Memo = React.memo(Component);

  return (props) =>
  (
    <Memo {...useResolvedProps(props)} />
  );
};


export const withClient = (Class) =>
{
  const DefaultContext = createContext();

  const Export = ({ as, children, ...props }) =>
  {
    const client = useClient(Class, props);

    const Context = as ? as : DefaultContext;

    return <Context value = {client} children = {children} />;
  };

  DefaultContext.Class = Class;
  DefaultContext.Export = Export;

  return DefaultContext;
};


export const withInstance = (Class) =>
{
  const DefaultContext = createContext();

  const Export = ({ as, children, ...props }) =>
  {
    const instance = useClient(Class, props);

    React.useEffect(() =>
    {
      instance.connect();

      return () => instance.disconnect();
    }, [ instance ]);

    const Context = as ? as : DefaultContext;

    return <Context value = {instance} children = {children} />;
  };

  DefaultContext.Class = Class;
  DefaultContext.Export = Export;
  DefaultContext.instance = true;

  return DefaultContext;
};


export const withValue = (callback) =>
{
  const DefaultContext = createContext();

  const Export = ({ as, children, ...props }) =>
  {
    const value = useValue(callback, props);

    const Context = as ? as : DefaultContext;

    return <Context value = {value} children = {children} />;
  };

  DefaultContext.Export = Export;

  return DefaultContext;
};


export const withStore = (defaultStateCallback, reducers) =>
{
  return withStoreExplicit(defaultStateCallback, reducers, useStore);
};


export const withAsyncStore = (defaultStateCallback, reducers) =>
{
  return withStoreExplicit(defaultStateCallback, reducers, useAsyncStore);
};


const withStoreExplicit = (defaultStateCallback, reducers, useStoreCallback) =>
{
  const DefaultContext = createStoreContext();

  const Export = ({ as, children, ...props }) =>
  {
    const [ state, dispatch ] = useStoreCallback(defaultStateCallback, reducers, props);

    const Context = as ? as : DefaultContext;

    return <Context.Dispatch value = {dispatch}><Context value = {state} children = {children} /></Context.Dispatch>;
  };

  DefaultContext.Export = Export;

  return DefaultContext;
};


// - - - - - Utility


const resolveReducer = (reducers, action) =>
{
  if (!reducers.hasOwnProperty(action.type))
  {
    throw new Error('Unsupported action type: ' + action.type);
  }

  return reducers[action.type];
};

